import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Building2, Mail, ArrowRight, Loader2, CheckCircle, Globe, Link2 } from 'lucide-react';
import api from '@/lib/api';
import {
  captureSignupAttribution,
  getSignupAttribution,
  clearSignupAttribution,
  recordSignupLifecycleEvent,
  captureClickToken,
  getClickToken,
  clearClickToken,
  getGa4AttributionFields,
} from '@/lib/signupAttribution';
import { trackGa4Event } from '@/lib/ga4';
import PasswordSetupFields from '@/components/PasswordSetupFields';
import OnboardingLoadingScreen from '@/components/OnboardingLoadingScreen';
import { passwordsMatch as computePasswordsMatch, allPasswordRequirementsMet } from '@/lib/passwordRequirements';

// How long to wait after the last keystroke, once the password is valid and
// confirmed, before auto-submitting the trusted-link instant signup -- long
// enough that a still-typing user doesn't get cut off mid-correction, short
// enough that it reads as "this just worked" rather than a stall.
const INSTANT_SIGNUP_AUTOSUBMIT_DEBOUNCE_MS = 900;


// Microsoft icon SVG
const MicrosoftIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 21 21">
    <rect x="1" y="1" width="9" height="9" fill="#f25022"/>
    <rect x="11" y="1" width="9" height="9" fill="#7fba00"/>
    <rect x="1" y="11" width="9" height="9" fill="#00a4ef"/>
    <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
  </svg>
);

// Logo URLs
const EVA_LOGO_URL = "/eva-icon-square.png";

// Public/free-mail domains that don't count as a company domain.
// Mirrors PUBLIC_EMAIL_DOMAINS in backend/routers/auth.py so the pre-submit
// warning matches the backend's domain-mismatch approval rule.
const PUBLIC_EMAIL_DOMAINS = new Set([
  'gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'aol.com',
  'icloud.com', 'mail.com', 'protonmail.com', 'zoho.com', 'yandex.com',
  'live.com', 'msn.com', 'me.com', 'mac.com', 'googlemail.com',
]);

// Mirrors normalize_domain in backend/routers/auth.py: lowercase, trim, drop a leading "www.".
const normalizeDomain = (domain) => {
  let d = (domain || '').toLowerCase().trim();
  if (d.startsWith('www.')) d = d.slice(4);
  return d;
};

// The next three mirror backend/routers/auth.py's own signup validators. The form carries
// noValidate, so the browser's native constraint checking no longer runs at all, and the 422
// handler deliberately replaces Pydantic's per-field detail with one generic sentence. Between
// them that left a real gap: values the backend rejects but the client waved through came back
// as "Please check your information and try again" with nothing naming the field at fault.
// Each returns the backend's own wording so the rule the visitor reads is the rule enforced.

// sanitize_company_name + validate_company_name: strip anything outside [A-Za-z0-9 \-&.,],
// collapse whitespace, then require 2+ characters and at least one letter. The client checked
// only for non-blank, so "A" and "123" both got through.
const checkCompanyName = (name) => {
  const sanitized = (name || '').replace(/[^a-zA-Z0-9\s\-&.,]/g, '').replace(/\s+/g, ' ').trim();
  if (sanitized.length < 2) return { ok: false, message: 'Company name must be at least 2 characters' };
  if (!/[a-zA-Z]/.test(sanitized)) return { ok: false, message: 'Company name must contain at least one letter' };
  return { ok: true };
};

// The first_name/last_name field_validator strips everything outside [A-Za-z \-'] and rejects
// what is left if no letter survives, so "123" was another value only the backend caught.
const hasLetter = (value) => /[a-zA-Z]/.test(value || '');

// EmailStr wants a deliverable-looking address: "a@" and "a@b" both fail it. includes('@') was
// enough while type=email ran first; with noValidate on the form, this stands in for it.
const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test((email || '').trim());

// Shared by both ways out of the Company Domain field so the two cannot drift apart.
const INVALID_DOMAIN_HINT = 'Please enter a valid domain (e.g., sony.com, google.com). Do not include http:// or www.';

export default function SignUpPage() {
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();
  
  const [formData, setFormData] = useState({
    company_name: '',
    first_name: '',
    last_name: '',
    email: '',
    company_domain: '',
  });
  const [loading, setLoading] = useState(false);
  const [socialLoading, setSocialLoading] = useState(null);
  const [success, setSuccess] = useState(false);
  const [instantOnboardingStarted, setInstantOnboardingStarted] = useState(false);
  const [instantOnboardingCompany, setInstantOnboardingCompany] = useState('');
  const [verificationPending, setVerificationPending] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');
  const [error, setError] = useState('');
  const [domainConflictTenant, setDomainConflictTenant] = useState(null);
  const [accessRequestSent, setAccessRequestSent] = useState(false);
  const [accessRequestLoading, setAccessRequestLoading] = useState(false);
  const [accessMessage, setAccessMessage] = useState('');
  
  // Referral state
  const [referralCode, setReferralCode] = useState('');
  const [referralInfo, setReferralInfo] = useState(null);
  const [referralLoading, setReferralLoading] = useState(false);
  
  // Company lookup state
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);

  // Company-name search-as-you-type (Brand Search API via GET /auth/company-search) -- the
  // primary way to find your company now, replacing "type your domain and click lookup".
  // Debounced with an AbortController so a fast typer's stale in-flight searches get cancelled
  // instead of racing to set companySearchResults out of order (same pattern used for this
  // same feature in eva-website's admin console).
  const [companySearchQuery, setCompanySearchQuery] = useState('');
  const [companySearchResults, setCompanySearchResults] = useState(null);
  const [companySearchLoading, setCompanySearchLoading] = useState(false);
  const [companySearchOpen, setCompanySearchOpen] = useState(false);
  // Keyboard position in the suggestion list. -1 means "on the input, nothing highlighted",
  // which is the state Enter's "none of these are it" meaning belongs to -- see the input's
  // onKeyDown. Anything >= 0 means a suggestion is active and Enter picks it instead.
  const [companySearchActiveIndex, setCompanySearchActiveIndex] = useState(-1);
  // Domain entry only ever appears on its own, when the typed Company Name doesn't resolve
  // (see the search-debounce effect below) -- never a manually-toggled field.
  const [showDomainField, setShowDomainField] = useState(false);
  const domainInputRef = useRef(null);
  // Guards handleCompanyLookup against firing more than once concurrently for the same field.
  // Enter and blur are two independent triggers for the same action (see handleDomainBlur's
  // comment), and companyConfirmed -- the only other guard -- doesn't flip true until the
  // in-flight request resolves, so a blur landing while an Enter-triggered lookup is still
  // pending re-ran it a second time; holding Enter did the same via the OS's own key-repeat,
  // since a plain keydown handler fires once per repeat with no debounce of its own. A ref
  // (not the lookupLoading state) is what's checked, since a same-tick second call needs to
  // see the "in flight" flag before React has committed the state update from the first.
  const lookupInFlightRef = useRef(false);
  // Whether the name-search dropdown panel (results or loading) should render -- only once
  // there's an actual query, not just on focus with an empty field.
  const searchPanelOpen = companySearchOpen && companySearchQuery.trim().length > 0;

  // Approval state - for domain mismatch signups
  const [requiresApproval, setRequiresApproval] = useState(false);
  const [existingTenantName, setExistingTenantName] = useState('');

  // First-party marketing attribution captured from eva-website's forwarded
  // eva_vid/eva_sid/UTM params, see src/lib/signupAttribution.js.
  const [signupStartedFired, setSignupStartedFired] = useState(false);

  // Trusted-link instant signup: a visitor arriving via a personalized,
  // HMAC-signed campaign link (?d=...) already has a known identity, so
  // skip company-lookup/manual-entry entirely and go straight to "set your
  // password" -- see POST /auth/resolve-click-token. auto-verify itself is
  // still decided server-side at actual signup time (signup()'s own email
  // match), this only controls what the UI shows before that.
  const [trustedIdentity, setTrustedIdentity] = useState(null);
  const [trustedCompanyInfo, setTrustedCompanyInfo] = useState(null);
  // True while a click token is being resolved (and, if it carries a domain,
  // while the follow-up Brandfetch company-lookup is also in flight). Keeps
  // the instant-signup card from rendering once unbranded and then redrawing
  // a moment later once accent color/logo show up -- render it once, fully
  // resolved, or not at all.
  //
  // Checks the raw ?d= URL param, not getClickToken() (sessionStorage) --
  // captureClickToken() only persists it to sessionStorage inside the mount
  // effect below, which hasn't run yet during this initial render, so
  // getClickToken() here would always read stale/empty and never engage on
  // a visitor's first load (exactly the flash this state exists to prevent).
  // getClickToken() is the fallback, not the primary read: after the mount
  // effect strips ?d= (EIP-433) a reload arrives with no query at all, and
  // without the fallback the trusted card would flash the manual form first.
  const [instantIdentityLoading, setInstantIdentityLoading] = useState(
    () => !!(searchParams.get('d') || getClickToken())
  );
  const [instantPassword, setInstantPassword] = useState('');
  const [instantConfirmPassword, setInstantConfirmPassword] = useState('');
  const [instantShowPassword, setInstantShowPassword] = useState(false);
  const [instantShowConfirmPassword, setInstantShowConfirmPassword] = useState(false);
  const [instantSubmitting, setInstantSubmitting] = useState(false);
  const [instantError, setInstantError] = useState('');
  // The exact password value the last (successful-or-failed) auto-submit
  // attempt used. Without this, a failed attempt (e.g. "account already
  // exists") left password/confirmPassword untouched and still valid+
  // matching, so the debounce effect below kept re-firing handleInstantSignup
  // in a tight loop -- the "flickering" a doomed retry produces (each attempt
  // toggles instantSubmitting and instantError on and off every ~900ms).
  const [instantAttemptedPassword, setInstantAttemptedPassword] = useState(null);

  const trackSignupStarted = () => {
    if (signupStartedFired) return;
    setSignupStartedFired(true);
    recordSignupLifecycleEvent('signup_started');
    trackGa4Event('signup_started', getGa4AttributionFields());
  };

  const handleResendVerification = async (emailAddr) => {
    setResendLoading(true);
    setResendSuccess(false);
    try {
      await api.post('/auth/resend-verification', { email: emailAddr });
      setResendSuccess(true);
      toast.success('Verification email sent', { description: 'Please check your inbox.' });
      // Reset success state after 30s so they can resend again
      setTimeout(() => setResendSuccess(false), 30000);
    } catch (_e) {
      toast.error('Error', { description: 'Could not resend email. Try again later.' });
    } finally {
      setResendLoading(false);
    }
  };
  const [companyInfo, setCompanyInfo] = useState(null);
  const [companyConfirmed, setCompanyConfirmed] = useState(false);
  const [lookupLoading, setLookupLoading] = useState(false);
  // Whether the Work Email field currently has focus -- see emailDomainMismatch's own comment
  // below for why this, not a timer, is what the prefix suppression is keyed on.
  const [emailFocused, setEmailFocused] = useState(false);

  // Focus Company Domain the moment it unveils itself -- the visitor was just typing in
  // Company Name, so carry their attention straight to the next thing to fill in rather than
  // leaving them to notice and click into it themselves.
  //
  // Keyed on companyConfirmed as well as showDomainField, because handleClearCompany's "Change"
  // button (on a *found* company's summary card) is a second way into this state and it does
  // not change showDomainField: a visitor who once saw the domain field left it open
  // (showDomainField stays true), so setShowDomainField(true) there would be a no-op and an
  // effect watching that alone never re-fires. What handleClearCompany does change is
  // companyConfirmed, true -> false, which also remounts the input (the whole search/domain
  // section renders behind !companyConfirmed), so the ref below is freshly attached by the time
  // this runs. Declared down here rather than up beside the ref because it reads companyConfirmed,
  // which useState does not hoist.
  useEffect(() => {
    if (showDomainField && !companyConfirmed) domainInputRef.current?.focus();
  }, [showDomainField, companyConfirmed]);

  const handleChange = (field) => (e) => {
    trackSignupStarted();
    setFormData(prev => ({ ...prev, [field]: e.target.value }));
    // Reset company confirmation if domain changes
    if (field === 'company_domain') {
      setCompanyInfo(null);
      setCompanyConfirmed(false);
    }
  };
  
  // Validate domain format
  const isValidDomain = (domain) => {
    if (!domain) return false;
    // Basic domain validation - must have at least one dot, no spaces, alphanumeric with hyphens
    const domainRegex = /^[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?)+$/;
    return domainRegex.test(domain.trim());
  };

  // Capture first-party marketing attribution (eva_vid/eva_sid/UTMs/etc.) on
  // first render and persist it across company lookup, validation errors,
  // and the Microsoft OAuth redirect, see src/lib/signupAttribution.js.
  useEffect(() => {
    captureSignupAttribution(searchParams);
    captureClickToken(searchParams);
    if (searchParams.get('d')) {
      // EIP-433: ?d= is a signed, single-use campaign token that grants
      // auto-verification and carries the prospect's identity. captureClickToken
      // has just put it in sessionStorage, so nothing below needs the query;
      // leaving it in the address bar only exposes it to history, the Referer
      // on every outbound link, and anything gtag sends about this page. Raw
      // replaceState so react-router's own state (idx, key) is spread through
      // untouched. Every other captured param goes with it, and they are all in
      // sessionStorage too (ATTRIBUTION_PARAMS in lib/signupAttribution.js).
      window.history.replaceState({ ...window.history.state }, document.title, window.location.pathname);
    }
    recordSignupLifecycleEvent('signup_viewed');
    trackGa4Event('signup_viewed', getGa4AttributionFields());

    // Resolve a trusted campaign link's identity, if present, so the page
    // can skip company-lookup/manual-entry entirely -- see
    // POST /auth/resolve-click-token.
    const clickToken = getClickToken();
    if (clickToken) {
      api.post('/auth/resolve-click-token', { click_token: clickToken }, {
        // Bounded for the same reason the manual flow's lookups are, and this is the call that
        // needs it most: instantIdentityLoading gates a full-page skeleton with no form beneath
        // it, and this chain's own .catch/.finally is the only thing that ever clears the flag.
        // The shared axios instance sets no default timeout (src/lib/api.js), so a stalled
        // request here strands a campaign-link visitor on a pulsing skeleton indefinitely --
        // strictly worse than the spinner that motivated the other two timeouts.
        timeout: 15000,
      })
        .then((response) => {
          if (!response.data.trusted) {
            setInstantIdentityLoading(false);
            return;
          }
          setTrustedIdentity(response.data);
          // Silently brand the instant-signup card (logo/colors) when the
          // token carries a known company domain -- same Brandfetch lookup
          // the manual flow uses, just without showing the domain-entry
          // step at all. Best-effort: a failed/empty lookup just means the
          // card renders with the generic Eva logo instead. Either way, the
          // loading flag only clears once this settles, so the card appears
          // fully formed instead of rendering once plain and redrawing a
          // moment later when the brand data arrives.
          if (response.data.domain) {
            api.post('/auth/company-lookup', { domain: response.data.domain }, { timeout: 15000 })
              .then((lookupResponse) => {
                if (lookupResponse.data.found) setTrustedCompanyInfo(lookupResponse.data);
              })
              .catch(() => {})
              .finally(() => setInstantIdentityLoading(false));
          } else {
            setInstantIdentityLoading(false);
          }
        })
        .catch(() => setInstantIdentityLoading(false));
    } else {
      // instantIdentityLoading's initializer accepts a raw ?d= param as well as a stored token,
      // but this chain is the only thing that ever clears it. captureClickToken() swallows a
      // sessionStorage write failure (blocked storage, a full private-mode quota) and
      // getClickToken() then reads back '', so a ?d= visitor in that state would start at true
      // and hold the skeleton forever without a single request having been made.
      setInstantIdentityLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Check for referral code in URL on mount. The stored fallback matters after
  // the ?d= strip above: on a reload the query is gone, but captureSignupAttribution
  // persisted ref along with the rest of the campaign params.
  useEffect(() => {
    const refCode = searchParams.get('ref') || getSignupAttribution().ref;
    if (refCode) {
      setReferralCode(refCode);
      validateReferralCode(refCode);
    }
  }, [searchParams]);

  // Validate referral code
  const validateReferralCode = async (code) => {
    if (!code) return;
    setReferralLoading(true);
    try {
      const response = await api.get(`/referrals/validate/${code}`);
      if (response.data.valid) {
        setReferralInfo(response.data);
      } else {
        setReferralInfo(null);
        toast.error('Invalid referral code', { description: response.data.message || 'This referral code is not valid' });
      }
    } catch (err) {
      console.error('Failed to validate referral code:', err);
      setReferralInfo(null);
    } finally {
      setReferralLoading(false);
    }
  };

  // Lookup company by domain -- domainOverride/nameOverride let a just-selected search
  // suggestion trigger this immediately with the right values, without waiting on a
  // setFormData update to flow back through this callback's closure first.
  //
  // keepCurrent is for a second lookup started from the not-found block's own Company Domain
  // field (retryDomainLookup below). The reset further down unconfirms the company before the
  // request goes out, which suits a first lookup, where nothing is on screen yet to lose. From
  // that block it does damage: the block unmounts for the length of the request, the original
  // Company Domain field remounts in its place, and the focus effect near the top focuses it,
  // taking focus from wherever the visitor was going and dropping it again when the lookup
  // returns. With keepCurrent the block stays put and the result replaces companyInfo on arrival.
  const handleCompanyLookup = useCallback(async (domainOverride, nameOverride, { keepCurrent = false } = {}) => {
    const domain = (domainOverride ?? formData.company_domain).trim();
    if (!domain) {
      toast.error('Domain required', { description: 'Please enter a company domain (e.g., sony.com)' });
      return;
    }

    // Validate domain format before making API call
    if (!isValidDomain(domain)) {
      toast.error('Invalid domain format', { description: INVALID_DOMAIN_HINT });
      return;
    }

    // A lookup is already in flight for this field (Enter's key-repeat, or a blur racing an
    // Enter that hasn't resolved yet) -- let that one finish rather than firing a duplicate.
    if (lookupInFlightRef.current) return;
    lookupInFlightRef.current = true;

    setLookupLoading(true);
    if (!keepCurrent) {
      setCompanyInfo(null);
      setCompanyConfirmed(false);
    }

    try {
      const response = await api.post('/auth/company-lookup', {
        domain,
        name: nameOverride || formData.company_name || '',
      }, {
        // The backend's own CRM round-trip is capped (10s), but this call has no cap of its
        // own -- if the request never reaches the backend, or the response never makes it
        // back (a stalled connection, a proxy that never times out), the spinner above would
        // otherwise spin forever with no way out. This bounds it to something a visitor won't
        // sit through, and lands in the same catch block as any other lookup failure below.
        timeout: 15000,
      });

      if (response.data.found) {
        setCompanyInfo(response.data);
        // Auto-fill company name if empty
        if (!formData.company_name && response.data.name) {
          setFormData(prev => ({ ...prev, company_name: response.data.name }));
        }
        // A resolved lookup (whether from a search suggestion or a domain lookup) already
        // identifies one specific company -- no separate "This is my company" click needed.
        setCompanyConfirmed(true);
        // response.data.name can be null -- a domain-confirmed lookup with no enriched name
        // data from the CRM (common for smaller/unlisted companies). Falls back to the name
        // this call was given, then whatever the visitor already typed, then the domain.
        // nameOverride comes first for the same reason it exists as a parameter at all: on the
        // selectCompanySearchResult path formData.company_name is still the pre-selection value
        // inside this tick, so reading it here would name the stale company (or, on a first
        // selection, drop to the bare domain) rather than the suggestion just picked.
        const foundName = response.data.name || nameOverride || formData.company_name || domain;
        toast.success('Company found!', { description: `Using ${foundName}.` });
      } else {
        setCompanyInfo({ found: false, domain });
        // Same auto-advance as the found branch above -- one continuous form, not a dead-end
        // panel with its own "Continue with manual entry" click. The manual Company Name
        // field's own helper text below explains why it's asking (see companyConfirmed block).
        setCompanyConfirmed(true);
        toast.error('Company not found', { description: response.data.message || "No brand information found for this domain -- that's fine, just fill in the details below." });
      }
    } catch (err) {
      console.error('Company lookup error:', err);
      toast.error('Lookup failed', { description: 'Unable to lookup company. You can still proceed with manual entry.' });
      setCompanyInfo({ found: false, domain, error: true });
      // Same auto-advance as both branches above, and the reason the toast just above can
      // promise manual entry at all. Without it a failure leaves companyConfirmed false, which
      // the manual Company Name field renders behind and which handleSubmit hard-blocks on, so
      // the visitor lands back on the search box with no way forward and no way to submit. A
      // failed lookup is precisely when the manual path has to work: the 15s timeout above
      // makes this branch reachable on any CRM outage, and the backend has always accepted a
      // company_domain it could not enrich.
      setCompanyConfirmed(true);
    } finally {
      lookupInFlightRef.current = false;
      setLookupLoading(false);
    }
  }, [formData.company_domain, formData.company_name]);

  // Debounced company-name search (Brand Search API via GET /auth/company-search) -- fires
  // while companySearchOpen is true (the search input has focus) and the query is non-blank.
  useEffect(() => {
    // Both bail-outs clear the spinner. The cleanup below aborts any in-flight request, and
    // the abort makes its own `finally` skip setCompanySearchLoading(false) on purpose, so
    // that mid-typing the spinner stays up across the handover to the next request. On the
    // paths where there is no next request -- the field lost focus, or the query was cleared
    // -- nothing else would ever turn it off, and it came back as a spinner on refocus.
    if (!companySearchOpen) { setCompanySearchLoading(false); return; }
    const query = companySearchQuery.trim();
    if (!query) { setCompanySearchResults(null); setCompanySearchLoading(false); return; }
    const controller = new AbortController();
    const timer = setTimeout(async () => {
      setCompanySearchLoading(true);
      try {
        const response = await api.get('/auth/company-search', {
          params: { q: query },
          signal: controller.signal,
          // Same reasoning as company-lookup's timeout above: bounds a stalled request so
          // the "Searching..." state can't spin forever if this one never comes back.
          timeout: 15000,
        });
        const results = response.data.results || [];
        setCompanySearchResults(results);
        // A new result set invalidates the old highlight; land back on the input so Enter
        // keeps meaning "none of these are it" until the visitor arrows into the list.
        setCompanySearchActiveIndex(-1);
        // Deliberately does NOT drive showDomainField -- it used to (toggled open whenever
        // results.length === 0), but a multi-word name like "Johnson and Melton LLC" pauses
        // >300ms at natural word boundaries while still being typed, and each of those
        // intermediate substrings independently matches-or-doesn't in Brandfetch's fuzzy
        // search. Driving visibility off that meant the field popped open and shut mid-
        // keystroke as the visitor was still composing the name. Visibility is now driven
        // only by an explicit "I'm done with this field" signal -- Enter (onKeyDown below)
        // or leaving the field (onBlur below) -- each a one-time decision, not something
        // every debounced keystroke can re-litigate.
      } catch (err) {
        if (err.name !== 'CanceledError' && err.code !== 'ERR_CANCELED') {
          setCompanySearchResults([]);
        }
      } finally {
        if (!controller.signal.aborted) setCompanySearchLoading(false);
      }
    }, 300);
    return () => { clearTimeout(timer); controller.abort(); };
  }, [companySearchQuery, companySearchOpen]);

  // Domain lookup fires on an explicit "I'm done with this field" signal -- Enter or blur --
  // never on a pause mid-typing. It used to run off a 500ms debounce, which looked like the
  // live-as-you-type feel the Company Name search above has, but the two fields are not alike:
  // a resolved lookup here sets companyConfirmed, and this whole section unmounts the moment it
  // does. isValidDomain has no TLD-length rule, so half-typed prefixes are already "valid"
  // (sony.c and sony.co both pass on the way to sony.com, and mail.sony passes on the way to
  // mail.sony.com). Anyone who paused half a second mid-word got confirmed onto the prefix, with
  // the input vanishing under their fingers and the CRM row written for the wrong domain --
  // while formData.company_domain kept the finished string that submit actually sends. Blur is
  // the same signal the Company Name field above already treats as "none of these are it", so
  // this is the consistent choice rather than a new concept.
  const handleDomainBlur = () => {
    if (companyConfirmed) return;
    const domain = formData.company_domain.trim();
    if (isValidDomain(domain)) {
      handleCompanyLookup(domain);
    } else if (domain) {
      // Enter already says this (handleCompanyLookup's own format check); blur is the other
      // way out of the field and used to say nothing at all. That silence is what made the
      // submit gate's message misleading: type "sony" with no dot, click away, and nothing
      // happens, then Create Account answers "Company domain required" while the field sits
      // there visibly filled in. An empty field stays silent, since leaving one alone is not
      // an error yet.
      toast.error('Invalid domain format', { description: INVALID_DOMAIN_HINT });
    }
  };

  // Company Domain stays visible (and editable) even after a not-found lookup -- see the
  // companyConfirmed && companyInfo && !companyInfo.found block below -- so a visitor can
  // correct a typo right there instead of starting over from "Change". That field's onChange only
  // updates formData.company_domain (unlike handleChange('company_domain'), which also resets
  // companyConfirmed/companyInfo on every keystroke -- doing that here, while this block is
  // still what's on screen, would unmount this input the instant a second character landed,
  // dropping focus onto a freshly-mounted duplicate a few keystrokes into typing). handleDomainBlur
  // above is not reusable here either: it opens with `if (companyConfirmed) return`, which is
  // exactly true in this state and would make blur a permanent no-op.
  //
  // Blur and Enter share this, and with it one rule for when to look again. A domain whose last
  // lookup gave an answer, found or not, is skipped (compared normalized), so passing through an
  // untouched field spends nothing. A lookup that errored gave no answer, so the same domain is
  // tried again; otherwise a timeout or a 429 could only be retried by retyping the domain.
  // handleCompanyLookup reports an empty or malformed value itself, but leaving an empty field
  // stays silent, the same as handleDomainBlur, since that is not an error yet.
  const retryDomainLookup = ({ fromEnter = false } = {}) => {
    const domain = formData.company_domain.trim();
    if (!domain && !fromEnter) return;
    const alreadyAnswered =
      !!domain && normalizeDomain(domain) === normalizeDomain(companyInfo?.domain) && !companyInfo?.error;
    if (alreadyAnswered) return;
    handleCompanyLookup(domain, undefined, { keepCurrent: true });
  };

  // A suggestion was picked -- fill in name+domain and immediately run the full lookup (which
  // also confirms/enriches via the CRM) rather than making the user click a second button.
  const selectCompanySearchResult = (result) => {
    setFormData(prev => ({ ...prev, company_domain: result.domain || prev.company_domain, company_name: result.name || prev.company_name }));
    setCompanySearchOpen(false);
    setCompanySearchResults(null);
    setCompanySearchQuery('');
    if (result.domain) {
      handleCompanyLookup(result.domain, result.name);
    }
  };


  // Clear company selection and start over -- used by the "Change" button on a *found*
  // company's summary card, where starting over means starting over completely: this is a
  // different company, so the old name is as stale as the old domain.
  const handleClearCompany = () => {
    setCompanyInfo(null);
    setCompanyConfirmed(false);
    setCompanySearchQuery('');
    setCompanySearchResults(null);
    setFormData(prev => ({ ...prev, company_domain: '', company_name: '' }));
  };

  // Warn before submit when the email's domain doesn't match the confirmed company
  // domain (the dominant approval trigger in backend/routers/auth.py). The only carve-out
  // there is on the COMPANY domain -- company_domain_for_tenant.lower() not in
  // PUBLIC_EMAIL_DOMAINS -- there is no matching carve-out for a public/free-mail EMAIL
  // domain; quite the opposite, "gmail user signing up for pfizer.com" is literally the
  // backend's own example of the case this exists to catch (approval_reason
  // "public_email_for_corporate_domain"). This used to also skip when the email side was
  // public, which meant the one warning most worth showing -- a personal email against a
  // real company domain -- was exactly the case it hid. Also skips while the email has no
  // domain part yet (e.g. "jane@").
  //
  // Compares against lookedUpDomain, never the field. formData.company_domain is live text
  // while the visitor is still typing an unconfirmed guess into Company Domain, and comparing the
  // email against that half-typed value produced the same premature-warning problem from the
  // other field. Requiring companyConfirmed used to be enough to rule that out, until the
  // not-found block's own Company Domain field became editable with the company still
  // confirmed; lookedUpDomain is what the last lookup checked, which covers both.
  //
  // Live, but suppressed while the typed domain is still a prefix of the confirmed one. That
  // is what a 600ms debounce was doing here before, and the debounce had a cost the prefix
  // test does not: it made the warning arrive in the gap between the visitor finishing the
  // email and reaching the submit button, which grows the card and (it is centred, see the
  // min-h-screen flex items-center wrapper) moves that button out from under a pointer already
  // travelling toward it. Playwright reported exactly that as three consecutive red E2E runs
  // once the debounce landed, and 59e9c391 then slept 700ms past it; a visitor gets the same
  // displacement with no retry. Keyed on the prefix instead, the card changes height while the
  // keyboard still has the visitor's hands, with the one exception set out at the end of this
  // comment.
  //
  // It also reads better than a timer. "d@air" on the way to "d@airproducts.com" is a partial
  // mismatch on every keystroke, which is the flashing the debounce existed to stop, but it is
  // also still a prefix, so nothing shows. The moment a character rules the confirmed domain
  // out, it shows, and stays shown: prefix-hood only ever breaks once while typing forward, so
  // there is nothing left to flicker.
  //
  // The prefix test alone had its own cost, though: "jane@acme.co" isn't necessarily an
  // unfinished "jane@acme.com" -- .co is a real TLD on its own, and a visitor could genuinely
  // work at a different company using exactly that shorter domain, indistinguishable from a
  // mid-keystroke prefix by the string test alone. Left running after the field is blurred, that
  // meant a complete, different, confirmed-not-matching domain got the same silence a half-typed
  // one does. emailFocused narrows the prefix carve-out to while the visitor might actually still
  // be typing -- the moment they leave the field, "still a prefix" stops being a good enough
  // reason to stay quiet, and a genuine mismatch shows immediately on blur, prefix or not. It is
  // still only advisory either way; the backend's own comparison (routers/auth.py) is an exact
  // match, never a prefix, so it decides approval regardless of what this warning did or didn't show.
  //
  // That is the exception mentioned above, and it is deliberate. For an email domain that is a
  // strict prefix of the company's, the blur that shows the warning can be the mousedown on
  // Create Account, so the card grows while the button is being pressed and the button moves.
  // When the click misses it, nothing is submitted and the warning is on screen, which is the
  // right outcome for what is most likely a typo ("acme.co" for "acme.com") and cheaper than an
  // account whose verification email goes nowhere. When the click still lands, the signup goes
  // ahead unwarned, the same as before emailFocused existed. Any test that submits with such an
  // email should assert the warning first, as the E2E signup helper does, rather than click blind.
  const emailDomain = normalizeDomain(formData.email.split('@')[1] || '');
  // The domain the last completed lookup checked, and whether the field still says the same.
  // companyConfirmed alone does not settle that: the not-found block's Company Domain can be
  // edited without unconfirming (unmounting that block mid-typing is the alternative), and a
  // re-lookup from it keeps the old answer on screen until the new one lands. Both warnings
  // describe a lookup, so both read lookedUpDomain; handleSubmit refuses to send a domain that
  // is not settled.
  const lookedUpDomain = companyConfirmed ? normalizeDomain(companyInfo?.domain) : '';
  const domainSettled = !!lookedUpDomain && lookedUpDomain === normalizeDomain(formData.company_domain);
  const emailDomainMismatch =
    !!emailDomain &&
    !!lookedUpDomain &&
    !PUBLIC_EMAIL_DOMAINS.has(lookedUpDomain) &&
    emailDomain !== lookedUpDomain &&
    (!emailFocused || !lookedUpDomain.startsWith(emailDomain));

  // Mirrors backend/routers/auth.py's OTHER approval trigger: `brand_info is None` requires
  // approval unconditionally (not just on an email/domain mismatch) -- signup() prefers a
  // false-positive approval over auto-approving a domain Brandfetch has never heard of. A
  // not-found company-lookup here is exactly that same "brand_info is None" outcome (both
  // paths call lookup_company_by_domain), so this account is going to need admin approval
  // regardless of whether the email happens to match the typed domain. Gated on lookedUpDomain
  // (not just companyInfo.found === false) so it can't fire before a domain exists to warn
  // about, and excludes public email domains the same way the backend's own guard does.
  //
  // companyInfo.error (the request itself failed: a timeout, a network error, or a 429 from this
  // endpoint's per-IP limit, which is raised before its try) is not that outcome. Nothing was
  // checked, and signup() runs its own lookup outside that limit, so the render below words it as
  // unchecked rather than promising an approval that may not come.
  const companyUnverified =
    companyConfirmed &&
    companyInfo?.found === false &&
    !!lookedUpDomain &&
    !PUBLIC_EMAIL_DOMAINS.has(lookedUpDomain);

  // Handle Microsoft OAuth callback
  useEffect(() => {
    const handleMicrosoftCallback = async () => {
      const code = searchParams.get('code');
      if (code) {
        setSocialLoading('processing');
        try {
          const redirectUri = window.location.origin + '/signup';
          const response = await api.post('/auth/microsoft/callback', {
            code,
            redirect_uri: redirectUri,
            attribution: getSignupAttribution(),
          });
          
          localStorage.setItem('eva_token', response.data.access_token);
          localStorage.setItem('eva_user', JSON.stringify(response.data.user));
          if (response.data.tenant) {
            localStorage.setItem('eva_tenant', JSON.stringify(response.data.tenant));
          }
          
          window.history.replaceState(null, '', '/signup');
          setSuccess(true);
          toast.success('Account created!', { description: 'Welcome to Eva Insights!' });
          setTimeout(() => {
            navigate('/');
            window.location.reload();
          }, 1500);
        } catch (err) {
          console.error('Microsoft OAuth error:', err);
          setError(err.response?.data?.detail || 'Microsoft sign-up failed. Please try again.');
          window.history.replaceState(null, '', '/signup');
        } finally {
          setSocialLoading(null);
        }
      }
    };
    
    handleMicrosoftCallback();
  }, [searchParams, navigate]);

  // Google sign-up was removed in EIP-319 (closes EIP-314). It was never a
  // Google integration: identity came from demobackend.emergentagent.com and the
  // backend endpoint trusted the email it was handed. See LoginPage for detail.

  // Trusted-link instant signup: signs the visitor up and completes the
  // account in one interaction, chaining POST /auth/signup (which returns
  // an already-auto-verified token for a trusted link) straight into
  // POST /auth/complete-signup -- no "check your email" step, no page
  // transition, no separate click required at all.
  const handleInstantSignup = useCallback(async () => {
    if (!trustedIdentity || instantSubmitting) return;
    setInstantSubmitting(true);
    setInstantError('');
    setInstantAttemptedPassword(instantPassword);
    try {
      const signupResponse = await api.post('/auth/signup', {
        company_name: trustedCompanyInfo?.name || trustedIdentity.company || 'My Company',
        first_name: trustedIdentity.first_name || 'there',
        last_name: trustedIdentity.last_name || '',
        email: trustedIdentity.email,
        company_domain: trustedIdentity.domain || undefined,
        attribution: getSignupAttribution(),
        click_token: getClickToken(),
      });

      if (signupResponse.data.status === 'pending_approval') {
        // This link already reached complete-signup once and is waiting on a
        // superadmin. The backend keeps that row as the approval vault and
        // returns this instead of a fresh token (see signup()'s pending-signup
        // check). There is nothing to resume: the token is spent, and dropping
        // the visitor back onto the form would only invite another attempt.
        clearClickToken();
        setTrustedIdentity(null);
        toast.info('Your account is pending approval.', {
          description: "You'll receive an email once your access has been approved.",
        });
        return;
      }

      if (!signupResponse.data.auto_verified || !signupResponse.data.verification_token) {
        // The email changed server-side, the token expired, or something
        // else broke trust between resolve-click-token and signup() --
        // fall back to the full flow rather than getting stuck.
        setTrustedIdentity(null);
        toast.info('Please complete your details to continue.');
        return;
      }

      const completeResponse = await api.post('/auth/complete-signup', {
        token: signupResponse.data.verification_token,
        password: instantPassword,
      });

      // Same domain-mismatch/Brandfetch-uncertainty approval gate signup()
      // uses for the manual flow can still fire here in principle, so guard
      // it the same way VerifyEmailPage does -- otherwise access_token/user
      // are undefined, JSON.stringify(undefined) writes the literal string
      // "undefined" to localStorage, and the next reload's JSON.parse throws,
      // silently leaving the user logged out (this was going straight to the
      // login page after a successful signup).
      if (completeResponse.status === 202 && completeResponse.data?.status === 'pending_approval') {
        // The token is spent: this signup is now waiting on a superadmin, and
        // a re-click must not look like a resume.
        clearClickToken();
        clearSignupAttribution();
        trackGa4Event('tenant_request_created', {
          ...getGa4AttributionFields(),
          request_status: 'pending',
        });
        setTrustedIdentity(null);
        toast.info('Your account is pending approval.', {
          description: "You'll receive an email once your access has been approved.",
        });
        return;
      }

      clearClickToken();
      clearSignupAttribution();
      localStorage.setItem('eva_token', completeResponse.data.access_token);
      localStorage.setItem('eva_user', JSON.stringify(completeResponse.data.user));
      if (completeResponse.data.tenant) {
        localStorage.setItem('eva_tenant', JSON.stringify(completeResponse.data.tenant));
      }
      trackGa4Event('signup_completed', {
        ...getGa4AttributionFields(),
        tenant_status: completeResponse.data.tenant?.subscription_status || '',
      });

      toast.success('Account created!', {
        description: `Welcome to Eva Insights, ${trustedIdentity.first_name}!`,
      });
      setInstantOnboardingCompany(trustedCompanyInfo?.name || trustedIdentity.company || '');
      setInstantOnboardingStarted(true);
    } catch (err) {
      const message = err.response?.data?.detail || 'Something went wrong. Please try again.';
      setInstantError(typeof message === 'string' ? message : 'Something went wrong. Please try again.');
      recordSignupLifecycleEvent('signup_submit_error', {
        error_code: String(err.response?.status || 'network_error'),
      });
      trackGa4Event('signup_submit_error', getGa4AttributionFields());
    } finally {
      setInstantSubmitting(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trustedIdentity, trustedCompanyInfo, instantPassword, instantSubmitting, navigate]);

  // Auto-start the trial the moment the password is valid and confirmed --
  // debounced so a still-typing/correcting user doesn't get cut off, but no
  // separate "click submit" step is required at all.
  useEffect(() => {
    if (!trustedIdentity) return;
    if (!allPasswordRequirementsMet(instantPassword)) return;
    if (!computePasswordsMatch(instantPassword, instantConfirmPassword)) return;
    if (instantSubmitting) return;
    // Already tried this exact value (success or failure) -- without this,
    // a failed attempt (e.g. "account already exists") left the password
    // fields untouched and still valid, so this effect kept re-firing
    // handleInstantSignup every debounce interval in an endless loop.
    if (instantPassword === instantAttemptedPassword) return;

    const timer = setTimeout(() => {
      handleInstantSignup();
    }, INSTANT_SIGNUP_AUTOSUBMIT_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [trustedIdentity, instantPassword, instantConfirmPassword, instantSubmitting, instantAttemptedPassword, handleInstantSignup]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.company_name.trim()) {
      toast.error('Company name required');
      recordSignupLifecycleEvent('signup_validation_error', { error_code: 'company_name_required' });
      trackGa4Event('signup_validation_error', getGa4AttributionFields());
      return;
    }
    const companyNameCheck = checkCompanyName(formData.company_name);
    if (!companyNameCheck.ok) {
      toast.error(companyNameCheck.message);
      recordSignupLifecycleEvent('signup_validation_error', { error_code: 'invalid_company_name' });
      trackGa4Event('signup_validation_error', getGa4AttributionFields());
      return;
    }
    if (!formData.first_name.trim() || !formData.last_name.trim()) {
      toast.error('First and last name required');
      recordSignupLifecycleEvent('signup_validation_error', { error_code: 'name_required' });
      trackGa4Event('signup_validation_error', getGa4AttributionFields());
      return;
    }
    if (!hasLetter(formData.first_name) || !hasLetter(formData.last_name)) {
      toast.error('First and last name must contain letters');
      recordSignupLifecycleEvent('signup_validation_error', { error_code: 'invalid_name' });
      trackGa4Event('signup_validation_error', getGa4AttributionFields());
      return;
    }
    if (!isValidEmail(formData.email)) {
      toast.error('Valid email required');
      recordSignupLifecycleEvent('signup_validation_error', { error_code: 'invalid_email' });
      trackGa4Event('signup_validation_error', getGa4AttributionFields());
      return;
    }
    // A company domain must be confirmed (found or not-found both set it, via
    // handleCompanyLookup) rather than left for the backend's email-domain fallback
    // (company_domain_for_tenant = request.company_domain or email_domain in
    // backend/routers/auth.py) to invent one. The personal-detail fields below are
    // always visible regardless of companyConfirmed, so without this check a visitor
    // could type a company name, ignore Company Domain entirely, and submit -- landing
    // a tenant on whatever happens to follow the "@" in their email, never actually
    // confirmed as a real company domain.
    //
    // A lookup still out comes first. There is no answer to submit against yet, and during a
    // first lookup companyConfirmed is false, so "required" would misdescribe a field that is
    // filled in with a spinner beside it. Pressing Create Account right after editing the
    // not-found block's Company Domain lands here: that press is the blur that starts the lookup.
    if (lookupInFlightRef.current) {
      toast.error('Still checking your company domain');
      recordSignupLifecycleEvent('signup_validation_error', { error_code: 'company_lookup_pending' });
      trackGa4Event('signup_validation_error', getGa4AttributionFields());
      return;
    }
    if (!companyConfirmed || !formData.company_domain.trim()) {
      // Title-only, matching the three checks above -- consistent, brief validation
      // feedback for every required field on this form.
      toast.error('Company domain required');
      recordSignupLifecycleEvent('signup_validation_error', { error_code: 'company_domain_required' });
      trackGa4Event('signup_validation_error', getGa4AttributionFields());
      return;
    }
    // Confirmed, but for a different value than the field now holds. A malformed edit to the
    // not-found block's Company Domain is never looked up (leaving the field only toasts), so it
    // stays confirmed under the old answer and, without this, went straight to /auth/signup, whose
    // validator only checks for a dot. The other way here is EIP-580's race, a suggestion picked
    // while a domain lookup was still out.
    if (!domainSettled) {
      const malformed = !isValidDomain(formData.company_domain);
      toast.error(malformed ? 'Invalid domain format' : 'Company domain not confirmed', {
        description: malformed ? INVALID_DOMAIN_HINT : 'Look your company up again before creating your account.',
      });
      recordSignupLifecycleEvent('signup_validation_error', {
        error_code: malformed ? 'invalid_company_domain' : 'company_domain_unconfirmed',
      });
      trackGa4Event('signup_validation_error', getGa4AttributionFields());
      return;
    }

    setLoading(true);
    setError('');
    try {
      const clickToken = getClickToken();
      const payload = {
        company_name: formData.company_name,
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email,
        attribution: getSignupAttribution(),
      };
      if (clickToken) {
        payload.click_token = clickToken;
      }

      // Only include company_domain if provided
      if (formData.company_domain.trim()) {
        payload.company_domain = formData.company_domain.trim();
      }

      // Include referral code if present
      if (referralCode && referralInfo?.valid) {
        payload.referral_code = referralCode;
      }

      const response = await api.post('/auth/signup', payload);
      clearClickToken(); // one-time use, regardless of outcome below

      // A stored click token still auto-verifies at signup even when
      // resolve-click-token failed earlier and sent this visitor down the
      // manual form, so the approval-vault response reaches this path too. It
      // carries no verification_token and no verification_required, so without
      // this it would fall through to the legacy branch below and write the
      // literal string "undefined" into localStorage.
      if (response.data.status === 'pending_approval') {
        clearSignupAttribution();
        setRegisteredEmail(formData.email);
        setRequiresApproval(true);
        setVerificationPending(true);
        toast.info('Your account is pending approval.', {
          description: "You'll receive an email once your access has been approved.",
        });
        return;
      }

      // Auto-verified: the signup email matched a personalized campaign
      // link's verified identity, so skip "check your email" entirely and
      // go straight to VerifyEmailPage's existing set-password step.
      if (response.data.auto_verified && response.data.verification_token) {
        clearSignupAttribution();
        navigate(`/verify-email?token=${response.data.verification_token}`);
        return;
      }

      // New flow: account created but verification required
      if (response.data.verification_required) {
        setRegisteredEmail(formData.email);
        setVerificationPending(true);

        // Check if this signup requires approval (domain mismatch)
        if (response.data.requires_approval) {
          setRequiresApproval(true);
          setExistingTenantName(response.data.existing_tenant_name || '');
          toast.info('Check your email', { description: 'Verify your email. Your account will then be reviewed for approval.' });
        } else {
          toast.info('Check your email', { description: 'We sent you a link to complete your signup.' });
        }
      } else {
        // Legacy flow (in case server returns tokens directly)
        localStorage.setItem('eva_token', response.data.access_token);
        localStorage.setItem('eva_user', JSON.stringify(response.data.user));
        localStorage.setItem('eva_tenant', JSON.stringify(response.data.tenant));

        clearSignupAttribution();
        setSuccess(true);
        toast.success('Account created!', { description: `Welcome to Eva Insights, ${formData.first_name}!` });

        setTimeout(() => {
          navigate('/');
          window.location.reload();
        }, 1500);
      }

    } catch (err) {
      recordSignupLifecycleEvent('signup_submit_error', {
        error_code: String(err.response?.status || 'network_error'),
      });
      trackGa4Event('signup_submit_error', getGa4AttributionFields());
      // Handle error - detail might be string or object
      let errorMessage = err.response?.data?.detail || err.message || 'An error occurred';
      if (typeof errorMessage === 'object') {
        // A 422 from FastAPI's own request-body validation (Pydantic) arrives as an array
        // of {msg, loc, ...} objects, one per invalid field, meant for API consumers, not
        // end users -- e.g. "Value error, Company name must be at least 2 characters".
        // The client-side checks above mirror the backend's rules field by field, so this
        // should be unreachable. It stays as a fail-safe for what those checks cannot
        // mirror (EmailStr's full deliverability rules, and anything added backend-side
        // without a matching check here), where a generic sentence still beats raw,
        // jargon-heavy validator text stitched into one run-on.
        if (Array.isArray(errorMessage)) {
          errorMessage = 'Please check your information and try again.';
        } else {
          errorMessage = errorMessage.msg || errorMessage.message || 'Please check your information and try again.';
        }
      }
      
      const lowerError = String(errorMessage).toLowerCase();
      const statusCode = err.response?.status;
      const detail = err.response?.data?.detail;
      if (statusCode === 409 && detail && typeof detail === 'object' && detail.tenant_id) {
        // Structured domain conflict with tenant info for Request Access
        setError('domain_conflict');
        setDomainConflictTenant({ id: detail.tenant_id, name: detail.tenant_name, domain: detail.domain });
      } else if (statusCode === 409 && lowerError.includes('domain')) {
        // Not 'domain_conflict': that code renders nothing without a tenant to go with it.
        // The generic banner below excludes the code by name and the Request Access card
        // requires domainConflictTenant, which only the branch above ever sets. The backend
        // sends tenant_id: null whenever the tenant owning the domain is not active
        // (check_domain_exists ignores status, the SELECT beside it filters on it), so this
        // is reachable, and it left the visitor watching the button stop spinning with no
        // message at all. Its own sentence is the useful thing to show.
        setError(errorMessage);
      } else if (lowerError.includes('already exists')) {
        setError('email_exists');
      } else if (lowerError.includes('pending verification')) {
        setError('pending_verification');
      } else {
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  // A click token is present and still resolving (identity + brand lookup).
  // Skeleton shaped like the eventual instant-signup card so there's no
  // flash-then-redraw once the accent color/logo/badge arrive -- nothing
  // renders below here until that's fully settled.
  if (instantIdentityLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background to-secondary flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-10 pb-10 animate-pulse">
            <div className="text-center mb-8">
              <div className="w-12 h-12 mx-auto mb-5 rounded-xl bg-muted" />
              <div className="h-7 w-48 mx-auto mb-2 rounded bg-muted" />
              <div className="h-4 w-64 mx-auto rounded bg-muted" />
            </div>
            <div className="space-y-5">
              <div className="h-10 rounded-xl bg-muted" />
              <div className="h-10 rounded-xl bg-muted" />
              <div className="h-10 rounded-xl bg-muted" />
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show loading state during OAuth processing
  if (socialLoading === 'processing') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background to-secondary flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-12 pb-12 text-center">
            <img src={EVA_LOGO_URL} alt="Eva" className="w-16 h-16 rounded-xl mx-auto mb-5" />
            <Loader2 className="w-8 h-8 animate-spin mx-auto text-primary mb-5" />
            <h2 className="text-xl font-semibold text-foreground">Creating your account...</h2>
            <p className="text-muted-foreground mt-2.5">Please wait while we set things up.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show verification pending screen
  if (verificationPending) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background to-secondary flex items-center justify-center p-4">
        <Card className="w-full max-w-md text-center">
          <CardContent className="pt-12 pb-12">
            <div className={`w-16 h-16 ${requiresApproval ? 'bg-warning/10' : 'bg-primary/10'} rounded-full flex items-center justify-center mx-auto mb-6`}>
              <Mail className={`w-8 h-8 ${requiresApproval ? 'text-warning-text' : 'text-primary'}`} />
            </div>
            <h2 className="text-2xl font-bold text-foreground mb-3">Check your email</h2>
            <p className="text-muted-foreground mb-4">
              We sent a verification link to <strong className="text-foreground">{registeredEmail}</strong>
            </p>

            {requiresApproval ? (
              <div className="bg-warning/10 border border-warning/30 rounded-xl p-4 mb-6 text-left">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-warning/10 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Building2 className="w-4 h-4 text-warning-text" />
                  </div>
                  <div>
                    <h4 className="font-medium text-warning-text text-sm">Subject to approval</h4>
                    <p className="text-sm text-warning-text mt-1">
                      {existingTenantName ? (
                        <>A company with this domain (<strong>{existingTenantName}</strong>) is already on Eva Insights. </>
                      ) : null}
                      After verifying your email, your account will be reviewed by an administrator. We'll notify you once your access is approved.
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground mb-8">
                Click the link in your email to activate your account. The link expires in 24 hours.
              </p>
            )}
            
            <div className="space-y-4">
              <Link to="/login">
                <Button variant="outline" className="w-full">
                  Back to login
                </Button>
              </Link>
              <div className="pt-2">
                {resendSuccess ? (
                  <p className="text-sm text-emerald-600 flex items-center justify-center gap-2">
                    <CheckCircle className="w-4 h-4" />
                    Verification email sent! Check your inbox.
                  </p>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    Didn't receive the email? Check your spam folder or{' '}
                    <button 
                      onClick={() => handleResendVerification(registeredEmail)}
                      disabled={resendLoading}
                      className="text-primary hover:underline disabled:opacity-50"
                    >
                      {resendLoading ? 'Sending...' : 'resend it'}
                    </button>
                  </p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Instant signup succeeded -- show the personality loading screen while
  // the backend best-effort seeds a starter KB doc, then redirect.
  if (instantOnboardingStarted) {
    return (
      <OnboardingLoadingScreen
        companyName={instantOnboardingCompany}
        onDone={() => {
          navigate('/');
          window.location.reload();
        }}
      />
    );
  }

  // Trusted-link instant signup: skip company-lookup/manual-entry entirely
  // for a visitor we already know from a personalized campaign link -- see
  // POST /auth/resolve-click-token and handleInstantSignup above.
  if (trustedIdentity) {
    const accentColor = trustedCompanyInfo?.primary_color || undefined;
    const companyName = trustedCompanyInfo?.name || trustedIdentity.company;
    const fullName = [trustedIdentity.first_name, trustedIdentity.last_name].filter(Boolean).join(' ');
    return (
      <div className="min-h-screen bg-gradient-to-br from-background to-secondary flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-10 pb-10">
            <div className="text-center mb-8">
              {/* Eva stays the primary mark. Company recognition is shown as
                  a separate icon alongside it (not a swap, and not squeezed
                  into a tiny corner badge -- a wordmark-style company icon
                  cropped into a 24px circle read as illegible mush in
                  practice). A connector glyph between the two reads as
                  "these are linked" rather than "Eva became this company". */}
              {trustedCompanyInfo?.icon_url ? (
                <div className="flex items-center justify-center gap-3 mb-5">
                  <img
                    src={EVA_LOGO_URL}
                    alt="Eva"
                    className="w-12 h-12 rounded-xl object-contain shrink-0"
                  />
                  <Link2 className="w-4 h-4 text-muted-foreground shrink-0" />
                  <img
                    src={trustedCompanyInfo.icon_url}
                    alt={companyName}
                    className="w-12 h-12 rounded-xl object-contain bg-white border border-border shadow-sm shrink-0"
                  />
                </div>
              ) : (
                <img
                  src={EVA_LOGO_URL}
                  alt="Eva"
                  className="w-12 h-12 rounded-xl object-contain mx-auto mb-5"
                />
              )}
              <h2 className="text-2xl font-bold text-foreground">
                Welcome, {fullName || 'there'}!
              </h2>
              <p className="text-muted-foreground mt-1.5">
                Set a password to start your free trial{companyName ? ` for ${companyName}` : ''}.
              </p>
            </div>

            {instantError && (
              <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-xl border border-destructive/30 mb-4">
                <p>{instantError}</p>
                {/* "Already exists" retrying with the same email would just
                    fail again identically -- point at the one action that
                    actually resolves it instead of offering a dead-end retry. */}
                {!instantError.toLowerCase().includes('already exists') && (
                  <button
                    type="button"
                    onClick={() => setInstantAttemptedPassword(null)}
                    className="text-destructive font-medium hover:underline mt-1.5"
                  >
                    Try again
                  </button>
                )}
              </div>
            )}

            <form
              onSubmit={(e) => { e.preventDefault(); handleInstantSignup(); }}
              className="space-y-5"
              autoComplete="on"
              name="instant-signup"
            >
              {/* Visually hidden but present in the DOM so the browser's
                  password manager has a username field to associate the
                  generated/saved credential with -- without one, its save
                  prompt shows a blank "Username" instead of the actual
                  email. Not a real input for the user: read-only, tab-skipped. */}
              <input
                type="email"
                name="username"
                autoComplete="username"
                value={trustedIdentity.email}
                readOnly
                tabIndex={-1}
                aria-hidden="true"
                className="sr-only"
              />
              <PasswordSetupFields
                password={instantPassword}
                setPassword={setInstantPassword}
                confirmPassword={instantConfirmPassword}
                setConfirmPassword={setInstantConfirmPassword}
                showPassword={instantShowPassword}
                setShowPassword={setInstantShowPassword}
                showConfirmPassword={instantShowConfirmPassword}
                setShowConfirmPassword={setInstantShowConfirmPassword}
              />

              <Button
                type="submit"
                className="w-full"
                style={accentColor ? { backgroundColor: accentColor } : undefined}
                disabled={
                  instantSubmitting ||
                  !allPasswordRequirementsMet(instantPassword) ||
                  !computePasswordsMatch(instantPassword, instantConfirmPassword)
                }
              >
                {instantSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Starting your trial...
                  </>
                ) : (
                  'Start My Free Trial'
                )}
              </Button>
              <p className="text-center text-xs text-muted-foreground">
                Not {trustedIdentity.first_name}?{' '}
                <button
                  type="button"
                  onClick={() => setTrustedIdentity(null)}
                  className="text-primary hover:underline"
                >
                  Sign up with a different account
                </button>
              </p>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background to-secondary flex items-center justify-center p-4">
        <Card className="w-full max-w-md text-center">
          <CardContent className="pt-12 pb-12">
            <div className="w-16 h-16 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-8 h-8 text-success" />
            </div>
            <h2 className="text-2xl font-bold text-foreground mb-3">Welcome aboard!</h2>
            <p className="text-muted-foreground mb-5">
              Your account has been created. Redirecting you to your dashboard...
            </p>
            <Loader2 className="w-5 h-5 animate-spin mx-auto text-primary" />
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-secondary flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center pb-6">
          {/* Logo */}
          <img src={EVA_LOGO_URL} alt="Eva" className="w-12 h-12 rounded-xl mx-auto mb-5" />
          <CardTitle className="text-2xl font-bold">Create your account</CardTitle>
          <CardDescription className="mt-1.5">
            Get started with Eva Insights for your company
          </CardDescription>
        </CardHeader>
        
        <CardContent>
          {/* Generic errors - hide internal codes like domain_conflict, email_exists, pending_verification */}
          {error && error !== 'email_exists' && error !== 'pending_verification' && error !== 'domain_conflict' && (
            <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-xl border border-destructive/30 mb-4">
              <div className="flex justify-between items-start">
                <span>{error}</span>
                <button 
                  type="button"
                  onClick={() => {
                    setError('');
                    setDomainConflictTenant(null);
                    setAccessRequestSent(false);
                    setAccessMessage('');
                  }}
                  className="text-xs underline hover:no-underline ml-2 whitespace-nowrap"
                >
                  Dismiss
                </button>
              </div>
            </div>
          )}

          {error === 'email_exists' && (
            <div className="bg-warning/10 border border-warning/30 rounded-xl p-4 mb-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-warning/10 rounded-full flex items-center justify-center flex-shrink-0">
                  <Mail className="w-5 h-5 text-warning-text" />
                </div>
                <div>
                  <h4 className="font-medium text-warning-text">Account already exists</h4>
                  <p className="text-sm text-warning-text mt-1">
                    An account with this email is already registered.
                  </p>
                  <Link 
                    to="/login" 
                    className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:text-primary mt-2"
                  >
                    Sign in to your account
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            </div>
          )}

          {error === 'domain_conflict' && domainConflictTenant && (
            <div className="bg-warning/10 border border-warning/30 rounded-xl p-4 mb-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-warning/10 rounded-full flex items-center justify-center flex-shrink-0">
                  <Building2 className="w-5 h-5 text-warning-text" />
                </div>
                <div className="flex-1">
                  <h4 className="font-medium text-warning-text">Company already registered</h4>
                  <p className="text-sm text-warning-text mt-1">
                    <strong>{domainConflictTenant.name}</strong> ({domainConflictTenant.domain}) is already on Eva Insights.
                  </p>

                  {!accessRequestSent && (
                    <div className="mt-4 space-y-3">
                      <textarea
                        placeholder="Optional: Tell the admin why you'd like to join..."
                        value={accessMessage}
                        onChange={(e) => setAccessMessage(e.target.value)}
                        className="w-full text-sm border rounded-lg p-2.5 bg-background resize-none h-16 focus:ring-1 focus:ring-primary/50 focus:border-primary/50"
                        data-testid="access-request-message"
                      />
                      <Button
                        type="button"
                        size="sm"
                        disabled={accessRequestLoading}
                        data-testid="request-access-btn"
                        onClick={async () => {
                          setAccessRequestLoading(true);
                          try {
                            await api.post('/auth/request-access', {
                              tenant_id: domainConflictTenant.id,
                              email: formData.email,
                              first_name: formData.first_name,
                              last_name: formData.last_name,
                              message: accessMessage,
                              attribution: getSignupAttribution(),
                            });
                            setAccessRequestSent(true);
                          } catch (e) {
                            const msg = e.response?.data?.detail || 'Could not submit request. Try again.';
                            toast.error('Error', { description: msg });
                          } finally {
                            setAccessRequestLoading(false);
                          }
                        }}
                        className="w-full"
                      >
                        {accessRequestLoading ? (
                          <><Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> Sending...</>
                        ) : (
                          <><Mail className="w-3.5 h-3.5 mr-1.5" /> Request Access to {domainConflictTenant.name}</>
                        )}
                      </Button>
                    </div>
                  )}

                  {accessRequestSent && (
                    <div className="mt-3 flex items-center gap-2 text-sm text-emerald-600">
                      <CheckCircle className="w-4 h-4" />
                      Request sent! The admin has been notified and will review it shortly.
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {error === 'pending_verification' && (
            <div className="bg-info/10 border border-info/30 rounded-xl p-4 mb-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-info/10 rounded-full flex items-center justify-center flex-shrink-0">
                  <Mail className="w-5 h-5 text-info" />
                </div>
                <div>
                  <h4 className="font-medium text-info">Verification pending</h4>
                  <p className="text-sm text-info mt-1">
                    This email is pending verification. Check your inbox for the verification link.
                  </p>
                  {resendSuccess ? (
                    <p className="text-sm text-emerald-600 flex items-center gap-1 mt-2">
                      <CheckCircle className="w-3.5 h-3.5" />
                      Email sent! Check your inbox.
                    </p>
                  ) : (
                    <button 
                      onClick={() => handleResendVerification(formData.email)}
                      disabled={resendLoading}
                      className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:text-primary mt-2 disabled:opacity-50"
                    >
                      {resendLoading ? (
                        <>
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        <>
                          Resend verification email
                          <ArrowRight className="w-4 h-4" />
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Referral Benefits Banner */}
          {referralInfo?.valid && (
            <div className="mb-4 p-4 rounded-lg bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800">
              <div className="flex items-start gap-3">
                <div className="h-8 w-8 rounded-full bg-emerald-100 dark:bg-emerald-900 flex items-center justify-center flex-shrink-0">
                  <CheckCircle className="h-4 w-4 text-emerald-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-emerald-800 dark:text-emerald-200">
                    {referralInfo.referrer_name ? `${referralInfo.referrer_name} invited you!` : 'You have a referral bonus!'}
                  </p>
                  <p className="text-sm text-emerald-700 dark:text-emerald-300 mt-1">
                    {referralInfo.benefits_summary}
                  </p>
                </div>
              </div>
            </div>
          )}
          {referralLoading && (
            <div className="mb-4 p-3 rounded-lg bg-muted flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span className="text-sm text-muted-foreground">Validating referral code...</span>
            </div>
          )}

          {/* Social Sign Up Buttons - Hidden until OAuth is configured
              To re-enable: uncomment the block below and remove this comment wrapper
          <>
          <div className="grid grid-cols-1 gap-3 mb-4">
            <Button
              type="button"
              variant="outline"
              disabled={true}
              className="h-10 opacity-60 cursor-not-allowed"
              data-testid="microsoft-signup-btn"
              title="Coming soon"
            >
              <MicrosoftIcon />
              <span className="ml-2">Microsoft</span>
            </Button>
          </div>
          <p className="text-xs text-center text-muted-foreground mb-4">Social login coming soon</p>

          <div className="relative mb-4">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-card px-2 text-muted-foreground">
                Or continue with email
              </span>
            </div>
          </div>
          </>
          */}

          {/* noValidate: handleSubmit's own checks below are the single source of truth for
              validation feedback (toast per field, all title-only for consistency). Without
              this, the browser's own native constraint validation (from type="email"/required)
              also fires -- a differently-worded, differently-styled tooltip stacked on top of
              our toast for the same underlying problem. */}
          <form onSubmit={handleSubmit} className="space-y-5" autoComplete="on" name="signup" noValidate>
            {/* Company lookup -- search box while unconfirmed, or the resolved state (found
                summary card / not-found manual name field) once it is. Personal-detail fields
                further below always render once companyConfirmed, right in this same
                form/scroll -- no separate step or page to navigate. */}
            {!companyConfirmed && (
              <>
                <div className="space-y-2 relative">
                  <Label htmlFor="company_search" className="flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-muted-foreground" />
                    Company Name
                  </Label>
                  <Input
                    id="company_search"
                    name="company_search"
                    type="text"
                    placeholder="Start typing your company name..."
                    value={companySearchQuery}
                    onChange={(e) => {
                      // Kept in sync with formData.company_name directly -- personal-detail
                      // fields below no longer wait for a resolved suggestion/domain lookup
                      // (one full form, not a gated step), so whatever's typed here is what
                      // submits if the visitor never picks a suggestion at all.
                      setCompanySearchQuery(e.target.value);
                      setFormData(prev => ({ ...prev, company_name: e.target.value }));
                      setCompanySearchOpen(true);
                    }}
                    onFocus={() => setCompanySearchOpen(true)}
                    onBlur={() => {
                      // Leaving the field without picking a suggestion is the same "none of
                      // these are it" signal Enter is (see onKeyDown below) -- a visitor who
                      // tabs/clicks away instead of pressing Enter still needs Company Domain
                      // to show up. Clicking a dropdown suggestion doesn't reach this at all
                      // (each button's onMouseDown already preventDefault()s, so the input
                      // never actually loses focus), so this can't fire right after a pick.
                      // The 150ms delay is just to let that mousedown run first regardless.
                      setTimeout(() => {
                        setCompanySearchOpen(false);
                        if (!companyConfirmed && companySearchQuery.trim()) {
                          setShowDomainField(true);
                        }
                      }, 150);
                    }}
                    onKeyDown={(e) => {
                      const results = companySearchResults || [];
                      // Arrow keys move through the suggestions without leaving the input, which
                      // is what lets aria-activedescendant carry the announcement. Wraps at both
                      // ends, and ArrowUp from the input goes to the last item.
                      if (e.key === 'ArrowDown' && results.length > 0) {
                        e.preventDefault();
                        setCompanySearchOpen(true);
                        setCompanySearchActiveIndex((i) => (i + 1) % results.length);
                        return;
                      }
                      if (e.key === 'ArrowUp' && results.length > 0) {
                        e.preventDefault();
                        setCompanySearchOpen(true);
                        setCompanySearchActiveIndex((i) => (i <= 0 ? results.length - 1 : i - 1));
                        return;
                      }
                      if (e.key === 'Escape') {
                        // Dismiss the list without committing to anything, and without the
                        // "none of these are it" consequence Enter carries.
                        e.preventDefault();
                        setCompanySearchOpen(false);
                        setCompanySearchActiveIndex(-1);
                        return;
                      }
                      if (e.key === 'Enter' && !companyConfirmed) {
                        e.preventDefault();
                        // With a suggestion highlighted, Enter picks it -- the whole point of
                        // the arrow keys above, and the only way to choose one without a mouse.
                        const active = results[companySearchActiveIndex];
                        if (active) {
                          selectCompanySearchResult(active);
                          return;
                        }
                        // Otherwise Enter keeps its original meaning: the explicit "none of
                        // these are it" signal. Brandfetch's fuzzy search often returns
                        // *something* for a real company name (just not the right something),
                        // so "results.length === 0" alone under-detects this. Deliberately NOT
                        // delegated to a highlighted-by-default first item, which is what a
                        // stock combobox would do and would make this signal unreachable.
                        setShowDomainField(true);
                        setCompanySearchOpen(false);
                      }
                    }}
                    autoComplete="off"
                    role="combobox"
                    aria-expanded={searchPanelOpen}
                    aria-controls="company-search-listbox"
                    aria-autocomplete="list"
                    aria-busy={companySearchLoading}
                    aria-activedescendant={
                      companySearchActiveIndex >= 0
                        ? `company-search-option-${companySearchActiveIndex}`
                        : undefined
                    }
                    data-testid="signup-company-search"
                  />
                  {/* Options are divs, not buttons: in a combobox the input keeps focus and
                      aria-activedescendant does the pointing, so options must not be tab stops
                      of their own. onMouseDown still preventDefault()s so a click never blurs
                      the input (the blur handler would otherwise fire mid-pick). */}
                  <div
                    id="company-search-listbox"
                    role="listbox"
                    aria-label="Company suggestions"
                    className={searchPanelOpen
                      ? "absolute z-10 mt-1 w-full rounded-lg border bg-card shadow-lg overflow-hidden"
                      : "hidden"}
                  >
                    {searchPanelOpen && companySearchLoading && (
                      <div className="px-3 py-2 text-sm text-muted-foreground flex items-center gap-2">
                        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Searching...
                      </div>
                    )}
                    {searchPanelOpen && !companySearchLoading && (companySearchResults || []).map((result, index) => (
                      <div
                        key={result.brand_id || result.domain}
                        id={`company-search-option-${index}`}
                        role="option"
                        aria-selected={index === companySearchActiveIndex}
                        tabIndex={-1}
                        onMouseDown={(e) => e.preventDefault()}
                        onMouseEnter={() => setCompanySearchActiveIndex(index)}
                        onClick={() => selectCompanySearchResult(result)}
                        className={`w-full flex items-center gap-2 px-3 py-2 text-left cursor-pointer ${
                          index === companySearchActiveIndex ? 'bg-muted' : 'hover:bg-muted/50'
                        }`}
                        data-testid="company-search-result"
                      >
                        {result.icon_url && (
                          <img src={result.icon_url} alt="" className="w-5 h-5 rounded object-contain" />
                        )}
                        <span className="text-sm font-medium">{result.name}</span>
                        <span className="text-xs text-muted-foreground truncate">{result.domain}</span>
                      </div>
                    ))}
                  </div>
                  {/* The listbox above is pointed at by aria-activedescendant, which announces
                      the highlighted option but not how many there are. This says that much,
                      politely, so a screen-reader user knows whether arrowing down is worth it. */}
                  <div className="sr-only" role="status" aria-live="polite">
                    {!searchPanelOpen ? '' :
                      companySearchLoading ? 'Searching for companies' :
                      companySearchResults === null ? '' :
                      companySearchResults.length === 0 ? 'No company suggestions. Press Enter to enter your domain instead.' :
                      `${companySearchResults.length} company ${companySearchResults.length === 1 ? 'suggestion' : 'suggestions'}. Use the arrow keys to review them.`}
                  </div>
                </div>

                {showDomainField && (
                  <div className="space-y-2 animate-in fade-in slide-in-from-top-2 duration-300">
                    <Label htmlFor="company_domain" className="flex items-center gap-2">
                      <Globe className="w-4 h-4 text-muted-foreground" />
                      Company Domain
                    </Label>
                    <div className="relative">
                      <Input
                        ref={domainInputRef}
                        id="company_domain"
                        name="company_domain"
                        type="text"
                        placeholder="e.g., sony.com, google.com"
                        value={formData.company_domain}
                        onChange={handleChange('company_domain')}
                        onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleCompanyLookup())}
                        onBlur={handleDomainBlur}
                        // Held while its own lookup runs. The lookup has already read this value,
                        // so anything typed on after Enter would reach formData but not the
                        // request, and a found result would confirm one domain while the summary
                        // card showed another.
                        readOnly={lookupLoading}
                        data-testid="signup-company-domain"
                      />
                      {/* No button -- Enter or leaving the field looks it up (see
                          handleDomainBlur above for why this is not debounced like the Company
                          Name search). This field is enrichment, not a gate: there's nothing
                          for a button to "advance" past. */}
                      {lookupLoading && (
                        // Centering (-translate-y-1/2) has to live on a wrapper, not the
                        // spinning element itself: animate-spin's keyframe sets `transform:
                        // rotate(360deg)` outright rather than composing with the translate
                        // utility's CSS var, so the two on one element made the browser
                        // interpolate between mismatched transform lists -- the icon visibly
                        // drifted off center over each rotation, then snapped back at the loop
                        // boundary, reading as a vertical "bounce".
                        <span className="absolute right-3 top-1/2 -translate-y-1/2">
                          <Loader2
                            className="w-4 h-4 animate-spin text-muted-foreground"
                            data-testid="lookup-company-spinner"
                          />
                        </span>
                      )}
                    </div>
                  </div>
                )}

              </>
            )}

            {/* Company Summary - shown when company was found. A small "brand card" in the
                Brandfetch gallery style (banner + overlapping logo tile + name + handle +
                description) rather than a plain icon-and-text row -- the lookup response
                carries no actual banner image, so primary_color/secondary_color stand in as
                a gradient where Brandfetch's own gallery would show a photo. */}
            {companyConfirmed && companyInfo?.found && (
                  <div className="rounded-xl border overflow-hidden mb-2" data-testid="company-summary-card">
                    <div
                      className="h-14 w-full bg-muted"
                      style={companyInfo?.primary_color ? {
                        background: `linear-gradient(135deg, ${companyInfo.primary_color}, ${companyInfo.secondary_color || companyInfo.primary_color})`,
                      } : undefined}
                    />
                    <div className="px-4 pb-4">
                      <div className="flex items-end justify-between -mt-7">
                        <div className="w-14 h-14 rounded-xl bg-card border-4 border-card shadow-sm overflow-hidden shrink-0">
                          {companyInfo?.icon_url || companyInfo?.logo_url ? (
                            <img
                              // icon_url is Brandfetch's square mark, made for exactly this
                              // square-tile placement -- logo_url is usually a wide wordmark
                              // that goes nearly illegible squeezed into a 56px square, so it's
                              // only the fallback for the (rare) brand with no icon variant.
                              src={companyInfo.icon_url || companyInfo.logo_url}
                              alt={companyInfo?.name || formData.company_name}
                              className="w-full h-full object-contain bg-white p-1.5"
                            />
                          ) : companyInfo?.primary_color ? (
                            <div
                              className="w-full h-full flex items-center justify-center text-white font-bold text-lg"
                              style={{ backgroundColor: companyInfo.primary_color }}
                            >
                              {(companyInfo?.name || formData.company_name)?.charAt(0) || 'C'}
                            </div>
                          ) : (
                            <div className="w-full h-full bg-primary/10 flex items-center justify-center">
                              <Building2 className="w-6 h-6 text-primary" />
                            </div>
                          )}
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={handleClearCompany}
                          className="text-muted-foreground hover:text-foreground mb-1 shrink-0"
                          data-testid="change-company-btn"
                        >
                          Change
                        </Button>
                      </div>
                      <div className="mt-3 flex items-center gap-1.5 min-w-0">
                        <p className="font-semibold truncate">{companyInfo?.name || formData.company_name || 'Your Company'}</p>
                        <CheckCircle className="w-4 h-4 text-primary shrink-0" />
                      </div>
                      <p className="text-xs text-muted-foreground">@{formData.company_domain}</p>
                      {companyInfo?.description && (
                        <p className="text-sm text-muted-foreground mt-2 line-clamp-2">
                          {companyInfo.description}
                        </p>
                      )}
                    </div>
                  </div>
            )}

            {/* Manual entry mode - company not found. Auto-advances here the moment the
                lookup resolves (see handleCompanyLookup) -- no separate confirm click. Company
                Domain stays on screen here (editable, via retryDomainLookup) rather than
                vanishing along with the search box: it's still exactly what gets submitted and
                still what the email-match warning below is comparing against, so hiding it left
                a visitor unable to see or fix the one value driving both. */}
            {companyConfirmed && companyInfo && !companyInfo.found && (
                  <div className="space-y-2 mb-2">
                    <Label htmlFor="company_name" className="flex items-center gap-2">
                      <Building2 className="w-4 h-4 text-muted-foreground" />
                      Company Name
                    </Label>
                    <Input
                      id="company_name"
                      name="organization"
                      placeholder="Acme Corporation"
                      value={formData.company_name}
                      onChange={handleChange('company_name')}
                      autoComplete="organization"
                      data-testid="signup-company-name"
                      required
                    />
                    <p className="text-xs text-muted-foreground">
                      Enter your company name (we couldn't find it automatically)
                    </p>

                    <Label htmlFor="company_domain_retry" className="flex items-center gap-2 mt-3">
                      <Globe className="w-4 h-4 text-muted-foreground" />
                      Company Domain
                    </Label>
                    <div className="relative">
                      <Input
                        id="company_domain_retry"
                        name="company_domain"
                        type="text"
                        placeholder="e.g., sony.com, google.com"
                        value={formData.company_domain}
                        onChange={(e) => setFormData(prev => ({ ...prev, company_domain: e.target.value }))}
                        onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), retryDomainLookup({ fromEnter: true }))}
                        onBlur={() => retryDomainLookup()}
                        // Same reason as the original field's readOnly, and here it also stops a
                        // second edit being dropped by lookupInFlightRef while the first is out.
                        readOnly={lookupLoading}
                        data-testid="signup-company-domain-retry"
                      />
                      {lookupLoading && (
                        // See the matching spinner in the initial Company Domain field above for
                        // why the translate lives on this wrapper span and not on the icon itself.
                        <span className="absolute right-3 top-1/2 -translate-y-1/2">
                          <Loader2
                            className="w-4 h-4 animate-spin text-muted-foreground"
                            data-testid="lookup-company-retry-spinner"
                          />
                        </span>
                      )}
                    </div>

                    {/* Mirrors backend/routers/auth.py's brand_verification_unavailable trigger --
                        see companyUnverified's own comment above. Suppressed once the more specific
                        email-mismatch warning near the Work Email field is showing, so a visitor
                        never reads two different "you'll need approval" explanations at once. */}
                    {companyUnverified && !emailDomainMismatch && (
                      <div className="bg-warning/10 border border-warning/30 rounded-xl p-3 mt-2" data-testid="signup-company-unverified-warning">
                        <div className="flex items-start gap-3">
                          <div className="w-8 h-8 bg-warning/10 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                            <Building2 className="w-4 h-4 text-warning-text" />
                          </div>
                          <div>
                            <h4 className="font-medium text-warning-text text-sm">Company not verified</h4>
                            <p className="text-sm text-warning-text mt-1">
                              {companyInfo.error ? (
                                <>We couldn't check <strong>@{lookedUpDomain}</strong> just now. You can continue, and if it still can't be confirmed when you create the account, an admin will need to approve it.</>
                              ) : (
                                <>We couldn't confirm <strong>@{lookedUpDomain}</strong> automatically. You can continue, but an admin will need to approve this account.</>
                              )}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
            )}

            {/* Personal details -- always visible, not gated on companyConfirmed at all. One
                full form: whatever's currently in the Company Name field above (typed or
                resolved) is what submits, so there's nothing to wait for here. */}
            <>
                {/* Name Fields */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="first_name">First Name</Label>
                    <Input
                      id="first_name"
                      name="given-name"
                      placeholder="John"
                      value={formData.first_name}
                      onChange={handleChange('first_name')}
                      autoComplete="given-name"
                      data-testid="signup-first-name"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="last_name">Last Name</Label>
                    <Input
                      id="last_name"
                      name="family-name"
                      placeholder="Doe"
                      value={formData.last_name}
                      onChange={handleChange('last_name')}
                      autoComplete="family-name"
                      data-testid="signup-last-name"
                      required
                    />
                  </div>
                </div>

                {/* Email */}
                <div className="space-y-2">
                  <Label htmlFor="email" className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-muted-foreground" />
                    Work Email
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="john@acme.com"
                    value={formData.email}
                    onChange={handleChange('email')}
                    onFocus={() => setEmailFocused(true)}
                    onBlur={() => setEmailFocused(false)}
                    autoComplete="email"
                    data-testid="signup-email"
                    required
                  />
                  {emailDomainMismatch && (
                    <div className="bg-warning/10 border border-warning/30 rounded-xl p-3" data-testid="signup-email-domain-warning">
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 bg-warning/10 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                          <Building2 className="w-4 h-4 text-warning-text" />
                        </div>
                        <div>
                          <h4 className="font-medium text-warning-text text-sm">Use your company email</h4>
                          <p className="text-sm text-warning-text mt-1">
                            Not a <strong>@{lookedUpDomain}</strong> address. You can continue, but an admin will need to approve this account.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Deliberately not disabled while the form is invalid. handleSubmit is what names
                    the field at fault and records signup_validation_error, and a disabled default
                    button reaches neither: it takes the click, and under the HTML Standard's
                    implicit submission rules it takes Enter too, leaving a grey button with no
                    reason, which is worst for input the validators reject outright (EIP-578). */}
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full eva-gradient"
                  data-testid="signup-submit-btn"
                >
                  {loading ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <ArrowRight className="w-4 h-4 mr-2" />
                  )}
                  {loading ? 'Creating account...' : 'Create Account'}
                </Button>
            </>

            {/* Login Link -- a real border-top + generous margin, not just more space-y rhythm,
                so this reads as "a separate thing below the form" rather than one more field. */}
            <p className="text-center text-sm text-muted-foreground mt-10 pt-6 border-t">
              Already have an account?{' '}
              <Link to="/login" className="text-primary hover:underline font-medium">
                Sign in
              </Link>
            </p>
            
            <p className="text-center text-xs text-muted-foreground mt-3">
              By signing up, you agree to our{' '}
              <a href="https://vistry.ai/tos" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                Terms of Service
              </a>
              {' '}and{' '}
              <a href="https://vistry.ai/privacy" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                Privacy Policy
              </a>
            </p>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
